<script setup lang="ts">
import { ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { useToast } from 'vue-toast-notification'
import { useSuiteStore } from '@/stores/useSuiteStore'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import { CodeBracketIcon, XMarkIcon, MagnifyingGlassIcon } from '@heroicons/vue/24/outline'

type WeatherStatus = 'OK' | 'WARNING' | 'KO' | 'UNKNOWN'

interface ServiceHealth {
  status: WeatherStatus
  lastChangedAt?: string | null
  lastSyncedAt?: string | null
  source?: string
  metadata?: Record<string, unknown> | null
}

interface Service {
  uuid: string
  name: string
  type: string
  externalReference?: string | null
  metadata?: Record<string, unknown> | null
  health?: ServiceHealth | null
}

const WEATHER_LABEL: Record<WeatherStatus, string> = {
  OK: 'OK',
  WARNING: 'Bagot',
  KO: 'KO',
  UNKNOWN: 'Inconnu',
}
const WEATHER_BADGE: Record<WeatherStatus, string> = {
  OK: 'badge-success',
  WARNING: 'badge-warning',
  KO: 'badge-error',
  UNKNOWN: 'badge-ghost',
}

const props = defineProps<{
  companyUuid?: string | null
}>()

const suiteStore = useSuiteStore()
const $toast = useToast()

const isSyncingKeyyo = ref(false)
const isSyncingNinjaOne = ref(false)
const isSyncingAlphalink = ref(false)
const services = ref<Service[]>([])
const isLoadingServices = ref(false)

const search = ref('')
let debounceTimer: ReturnType<typeof setTimeout> | null = null

const selectedMetadata = ref<Record<string, unknown> | null>(null)
const selectedServiceName = ref('')

function openMetadata(service: Service) {
  selectedServiceName.value = service.name
  selectedMetadata.value = service.metadata ?? null
}

function closeMetadata() {
  selectedMetadata.value = null
  selectedServiceName.value = ''
}

async function fetchServices() {
  if (!props.companyUuid) return
  isLoadingServices.value = true
  try {
    const s = search.value.trim()
    const body: Record<string, unknown> = {
      filters: { companyUuid: props.companyUuid },
      ...(s ? { search: s } : {}),
    }
    const res = await suiteStore.suiteFetch('service/list', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const data = await res.json()
      services.value = Array.isArray(data) ? data : (data?.data ?? [])
    }
  } catch {
    // silencieux
  } finally {
    isLoadingServices.value = false
  }
}

watch(() => props.companyUuid, fetchServices, { immediate: true })

watch(search, () => {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(fetchServices, 350)
})

async function onSyncKeyyo() {
  if (!props.companyUuid) return $toast.error('Aucune société associée.')
  isSyncingKeyyo.value = true
  try {
    const res = await suiteStore.suiteFetch(`service/synchronize-keyyo/${props.companyUuid}`, {
      method: 'POST',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      $toast.success('Synchronisation Keyyo effectuée.')
      await fetchServices()
    } else {
      $toast.error('Erreur lors de la synchronisation Keyyo.')
    }
  } catch {
    $toast.error('Erreur lors de la synchronisation Keyyo.')
  } finally {
    isSyncingKeyyo.value = false
  }
}

async function onSyncNinjaOne() {
  if (!props.companyUuid) return $toast.error('Aucune société associée.')
  isSyncingNinjaOne.value = true
  try {
    const res = await suiteStore.suiteFetch(`service/synchronize-ninjaone/${props.companyUuid}`, {
      method: 'POST',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      $toast.success('Synchronisation NinjaOne effectuée.')
      await fetchServices()
    } else {
      $toast.error('Erreur lors de la synchronisation NinjaOne.')
    }
  } catch {
    $toast.error('Erreur lors de la synchronisation NinjaOne.')
  } finally {
    isSyncingNinjaOne.value = false
  }
}

async function onSyncAlphalink() {
  if (!props.companyUuid) return $toast.error('Aucune société associée.')
  isSyncingAlphalink.value = true
  try {
    const res = await suiteStore.suiteFetch(`service/synchronize-alphalink/${props.companyUuid}`, {
      method: 'POST',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      $toast.success('Synchronisation Alphalink effectuée.')
      await fetchServices()
    } else {
      $toast.error('Erreur lors de la synchronisation Alphalink.')
    }
  } catch {
    $toast.error('Erreur lors de la synchronisation Alphalink.')
  } finally {
    isSyncingAlphalink.value = false
  }
}
</script>

<template>
  <div class="card bg-base-100 shadow-sm border border-base-200">
    <div class="card-body p-4 md:p-6">
      <div class="flex items-center justify-between flex-wrap gap-4 border-b pb-4 mb-6">
        <div>
          <h2 class="card-title">Services (Super Admin)</h2>
          <p class="text-sm text-base-content/60">
            Synchronisation des services pour la société associée.
          </p>
        </div>
      </div>

      <!-- Boutons de synchronisation -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div class="p-4 bg-base-200/50 rounded-lg flex flex-col justify-between gap-3">
          <div>
            <h3 class="font-semibold text-sm mb-1">Keyyo</h3>
            <p class="text-xs text-base-content/60">
              Importe et met à jour les services téléphonie Keyyo liés à la société.
            </p>
          </div>
          <ButtonDaisy
            text="Synchroniser Keyyo"
            variant="warning"
            class="w-full"
            :loader="isSyncingKeyyo"
            :disabled="isSyncingKeyyo || isSyncingNinjaOne || isSyncingAlphalink || !companyUuid"
            @click="onSyncKeyyo"
          />
        </div>

        <div class="p-4 bg-base-200/50 rounded-lg flex flex-col justify-between gap-3">
          <div>
            <h3 class="font-semibold text-sm mb-1">NinjaOne</h3>
            <p class="text-xs text-base-content/60">
              Importe et met à jour les devices NinjaOne liés à la société.
            </p>
          </div>
          <ButtonDaisy
            text="Synchroniser NinjaOne"
            variant="warning"
            class="w-full"
            :loader="isSyncingNinjaOne"
            :disabled="isSyncingKeyyo || isSyncingNinjaOne || isSyncingAlphalink || !companyUuid"
            @click="onSyncNinjaOne"
          />
        </div>

        <div class="p-4 bg-base-200/50 rounded-lg flex flex-col justify-between gap-3">
          <div>
            <h3 class="font-semibold text-sm mb-1">Alphalink</h3>
            <p class="text-xs text-base-content/60">
              Importe et met à jour les services Alphalink (fibre/data) liés à la société.
            </p>
          </div>
          <ButtonDaisy
            text="Synchroniser Alphalink"
            variant="warning"
            class="w-full"
            :loader="isSyncingAlphalink"
            :disabled="isSyncingKeyyo || isSyncingNinjaOne || isSyncingAlphalink || !companyUuid"
            @click="onSyncAlphalink"
          />
        </div>
      </div>

      <!-- Liste des services -->
      <div class="mt-6">
        <div class="flex items-center justify-between gap-4 mb-3">
          <h3 class="font-semibold text-sm">Services associés</h3>
          <label class="input input-sm input-bordered flex items-center gap-2 w-64">
            <MagnifyingGlassIcon class="h-3.5 w-3.5 opacity-50 shrink-0" />
            <input
              v-model="search"
              type="text"
              class="grow"
              placeholder="Nom ou métadonnée…"
            />
          </label>
        </div>

        <div v-if="isLoadingServices" class="flex justify-center py-6">
          <span class="loading loading-spinner loading-md"></span>
        </div>

        <div v-else-if="services.length === 0" class="py-6 text-center text-sm text-base-content/50">
          Aucun service trouvé pour cette société.
        </div>

        <div v-else class="overflow-x-auto">
          <table class="table table-sm table-zebra">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Type</th>
                <th>Référence externe</th>
                <th>Météo</th>
                <th>Métadonnées</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="service in services" :key="service.uuid">
                <td class="font-medium">{{ service.name }}</td>
                <td><span class="badge badge-ghost badge-sm">{{ service.type }}</span></td>
                <td class="font-mono text-xs text-base-content/70">{{ service.externalReference ?? '-' }}</td>
                <td>
                  <RouterLink
                    v-if="service.health && service.externalReference"
                    :to="`/admin/fiber-links/${encodeURIComponent(service.externalReference)}`"
                    class="badge badge-sm cursor-pointer"
                    :class="WEATHER_BADGE[service.health.status]"
                    :title="service.health.lastSyncedAt ? `Dernière sync : ${new Date(service.health.lastSyncedAt).toLocaleString('fr-FR')}` : ''"
                  >
                    {{ WEATHER_LABEL[service.health.status] }}
                  </RouterLink>
                  <span v-else class="text-xs text-base-content/40">—</span>
                </td>
                <td>
                  <button
                    v-if="service.metadata"
                    class="btn btn-ghost btn-xs gap-1"
                    title="Voir les métadonnées"
                    @click="openMetadata(service)"
                  >
                    <CodeBracketIcon class="h-3.5 w-3.5" />
                    Voir
                  </button>
                  <span v-else class="text-xs text-base-content/40">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal métadonnées -->
  <div v-if="selectedMetadata !== null" class="modal modal-open">
    <div class="modal-box w-11/12 max-w-2xl">
      <div class="flex items-center justify-between mb-4">
        <h3 class="font-bold text-lg">Métadonnées — {{ selectedServiceName }}</h3>
        <button class="btn btn-sm btn-ghost btn-circle" @click="closeMetadata">
          <XMarkIcon class="h-4 w-4" />
        </button>
      </div>

      <pre class="bg-base-200 rounded-lg p-4 text-xs overflow-auto max-h-[60vh] whitespace-pre-wrap break-all">{{ JSON.stringify(selectedMetadata, null, 2) }}</pre>

      <div class="modal-action">
        <button class="btn btn-sm" @click="closeMetadata">Fermer</button>
      </div>
    </div>
    <div class="modal-backdrop" @click="closeMetadata" />
  </div>
</template>
